import {
  readOnboardingProgress,
  writeOnboardingProgress,
} from "@/utils/onboardingStorage";
import { USER_ACTION } from "@/utils/userActions";

export const ONBOARDING_STEPS = 3;

// Когда онбординг появился в клиенте. Обычный список старше этой даты —
// верный признак старого аккаунта. По задачам так судить нельзя: демо-задачи
// создаются при регистрации, и у аккаунта, заведённого раньше этой даты, но
// без единого списка, они тоже «старые» — а по ТЗ такой аккаунт новый.
export const ONBOARDING_RELEASED_AT = Date.parse("2026-10-20T00:00:00+05:00");

const emptyProgress = () => ({
  // Новый ли пользователь; null — ещё не решено. Решается один раз: после
  // первого созданного списка признак «нет ни одного списка» ложен, а
  // карточка должна остаться до 3/3.
  eligible: null,
  // онбординг-список: созданный через карточку или первый созданный сам
  listId: null,
  taskDone: false,
  inviteDone: false,
  // нажато «Hide»
  hidden: false,
  // Все три шага были выполнены. Хранится отдельно, чтобы удаление
  // онбординг-списка после 3/3 не вернуло карточку.
  completed: false,
});

const initialState = () => ({
  userId: null,
  ...emptyProgress(),
  // список, в котором AddTasks должен сфокусировать поле новой задачи
  taskFocusListId: null,
});

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

// date на сервере — строка Date.now ("1760900000000"), с клиента бывает ISO
function toMs(value) {
  if (value == null || value === "") return NaN;
  const number = Number(value);
  return Number.isFinite(number) ? number : Date.parse(value);
}

const createdBeforeRelease = (item) =>
  Boolean(item) && toMs(item.date) < ONBOARDING_RELEASED_AT;

// обычный (не Inbox) список старше онбординга
function hasLegacyList(projects, inboxId) {
  return projects.some(
    (p) => p && !sameId(p._id, inboxId) && createdBeforeRelease(p)
  );
}

function listExists(rootGetters, listId) {
  const projects = (rootGetters && rootGetters.getProjects) || [];
  return Boolean(listId) && projects.some((p) => p && sameId(p._id, listId));
}

function persist(state) {
  writeOnboardingProgress(state.userId, {
    eligible: state.eligible,
    listId: state.listId,
    taskDone: state.taskDone,
    inviteDone: state.inviteDone,
    hidden: state.hidden,
    completed: state.completed,
  });
}

export default {
  state: initialState,
  mutations: {
    setOnboardingSession(state, { userId, progress }) {
      Object.assign(state, initialState(), progress, { userId });
    },
    resetOnboardingSession(state) {
      Object.assign(state, initialState());
    },
    setOnboardingProgress(state, progress) {
      Object.assign(state, progress);
      persist(state);
    },
    setOnboardingTaskFocus(state, listId) {
      state.taskFocusListId = listId == null ? null : String(listId);
    },
  },
  actions: {
    // Решить, новый ли пользователь, — по спискам из ответа сервера.
    //
    // projects — слепок с сервера, а не стор: в сторе к этому моменту списки
    // могут быть ещё не разложены, и пустой стор выдал бы старого
    // пользователя за нового. projects: null — решать не по чему.
    //
    // Пока онбординг не начат (ни шага, ни Hide), eligible пересчитывается
    // при каждой загрузке: сохранённое решение могло быть принято по чужому
    // или пустому стору (прежний баг) и не должно залипать ни в true, ни в
    // false. Начавший онбординг видит карточку до 3/3 или Hide, даже когда
    // у него появились списки. Обычный список старше онбординга (hasLegacyList)
    // убирает карточку в любом случае.
    configureOnboarding(
      { commit, state },
      { userId, projects = null, inboxId = null }
    ) {
      if (!userId) {
        commit("resetOnboardingSession");
        return;
      }
      userId = String(userId);
      if (state.userId != userId) {
        commit("setOnboardingSession", {
          userId,
          progress: readOnboardingProgress(userId) || emptyProgress(),
        });
      }
      if (!Array.isArray(projects)) return;

      // Старый аккаунт: карточку убираем, даже если прежний баг успел
      // засчитать ему шаги.
      if (hasLegacyList(projects, inboxId)) {
        if (state.eligible !== false) {
          commit("setOnboardingProgress", { eligible: false });
        }
        return;
      }

      const started =
        state.eligible === true &&
        Boolean(
          state.listId ||
            state.taskDone ||
            state.inviteDone ||
            state.hidden ||
            state.completed
        );
      if (started) return;

      // Онбординг-список мог появиться до загрузки — он свой, а не
      // «ранее существовавший».
      const hasRegularList = projects.some(
        (p) =>
          p && p._id && !sameId(p._id, inboxId) && !sameId(p._id, state.listId)
      );
      if (state.eligible !== !hasRegularList) {
        commit("setOnboardingProgress", { eligible: !hasRegularList });
      }
    },

    // Слушатель шины пользовательских действий (@/utils/userActions).
    // Шаги засчитываются по факту действия, а не по пути, которым к нему
    // пришли: задача в любом списке закрывает шаг так же, как созданная из
    // карточки.
    reportUserAction({ commit, state, rootGetters }, { type, projectId } = {}) {
      // старому пользователю и закрытой карточке считать нечего
      if (!state.userId || state.eligible === false) return;
      if (state.hidden || state.completed) return;

      const next = {};
      if (type == USER_ACTION.LIST_CREATED) {
        // Онбординг-список — первый созданный. Новый занимает его место,
        // только если прежний удалён: иначе пункты вели бы в пустоту.
        if (projectId && !listExists(rootGetters, state.listId)) {
          next.listId = String(projectId);
        }
      } else if (type == USER_ACTION.TASK_CREATED) {
        if (!state.taskDone) next.taskDone = true;
      } else if (type == USER_ACTION.LIST_MEMBER_ADDED) {
        if (!state.inviteDone) next.inviteDone = true;
      }
      if (Object.keys(next).length == 0) return;

      const progress = { ...state, ...next };
      if (
        progress.taskDone &&
        progress.inviteDone &&
        listExists(rootGetters, progress.listId)
      ) {
        next.completed = true;
      }
      commit("setOnboardingProgress", next);
    },

    hideOnboarding({ commit, state }) {
      if (!state.userId || state.hidden) return;
      commit("setOnboardingProgress", { hidden: true });
    },

    // Пункт «Add your first task»: открыть список и попросить AddTasks
    // сфокусировать существующее поле добавления задачи.
    requestOnboardingTaskFocus({ commit }, listId) {
      commit("setOnboardingTaskFocus", listId);
    },
    clearOnboardingTaskFocus({ commit }) {
      commit("setOnboardingTaskFocus", null);
    },

    resetOnboardingSession({ commit }) {
      commit("resetOnboardingSession");
    },
  },
  getters: {
    getOnboardingState: (state) => state,
    getOnboardingListExists: (state, getters, rootState, rootGetters) =>
      listExists(rootGetters, state.listId),
    getOnboardingCompletedCount: (state, getters) =>
      Number(getters.getOnboardingListExists) +
      Number(state.taskDone) +
      Number(state.inviteDone),
    // До решения «новый ли» (то есть до загрузки данных) карточки нет —
    // иначе она мигала бы у существующих пользователей.
    getOnboardingVisible: (state, getters) =>
      state.eligible === true &&
      !state.hidden &&
      !state.completed &&
      getters.getOnboardingCompletedCount < ONBOARDING_STEPS,
    getOnboardingTaskFocus: (state) => state.taskFocusListId,
  },
};
